import { useState } from 'react';
import { attendanceAPI } from '../lib/api';
import { toast } from 'sonner';

/**
 * Owns the kiosk clock-in / clock-out flow:
 *   1. Employee selected → PIN modal
 *   2. PIN verified → action modal
 *   3. Action triggered → API call → confirmation modal
 *
 * Pulled out of Kiosk.jsx so the page component focuses on layout and the
 * flow becomes independently testable.
 */
export function useKioskClockFlow({ onEmployeesShouldRefresh } = {}) {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [confirmationWarning, setConfirmationWarning] = useState('');

  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [verifyingPin, setVerifyingPin] = useState(false);
  const [verifiedPin, setVerifiedPin] = useState(null);

  const [processing, setProcessing] = useState(false);

  // ----- Employee selection -----
  const startFlow = (employee) => {
    setSelectedEmployee(employee);
    setShowPinModal(true);
    setPin('');
    setPinError('');
  };

  // ----- PIN handlers -----
  const handlePinDigit = (digit) => {
    if (pin.length < 6) {
      setPin((p) => p + digit);
      setPinError('');
    }
  };
  const handlePinBackspace = () => {
    setPin((p) => p.slice(0, -1));
    setPinError('');
  };
  const handlePinClear = () => {
    setPin('');
    setPinError('');
  };

  const handleVerifyPin = async () => {
    if (pin.length !== 6) {
      setPinError('Please enter 6 digits');
      return;
    }
    if (!selectedEmployee) return;

    setVerifyingPin(true);
    try {
      const response = await attendanceAPI.verifyPin({
        employee_id: selectedEmployee.employee_id,
        pin,
      });
      if (response.data.valid) {
        setVerifiedPin(pin);
        setShowPinModal(false);
        setShowActionModal(true);
        setPin('');
      } else {
        setPinError('Incorrect PIN. Please try again.');
        setPin('');
      }
    } catch (error) {
      setPinError(error.response?.data?.detail || 'Verification failed');
      setPin('');
    } finally {
      setVerifyingPin(false);
    }
  };

  const handleCancelPin = () => {
    setShowPinModal(false);
    setSelectedEmployee(null);
    setPin('');
    setPinError('');
    setVerifiedPin(null);
  };

  // ----- Action handlers -----
  const handleClockAction = async (action) => {
    if (!selectedEmployee) return;
    setProcessing(true);
    try {
      const response = await attendanceAPI.clock({
        employee_id: selectedEmployee.employee_id,
        action,
      });

      setConfirmationMessage(response.data.message);
      setConfirmationWarning(response.data.warning || '');
      setShowActionModal(false);
      setShowConfirmation(true);
      setSelectedEmployee(null);
      setVerifiedPin(null);

      const autoCloseMs = response.data.warning ? 7000 : 3000;
      setTimeout(() => {
        setShowConfirmation(false);
        setConfirmationMessage('');
        setConfirmationWarning('');
      }, autoCloseMs);

      onEmployeesShouldRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record attendance');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelAction = () => {
    setShowActionModal(false);
    setSelectedEmployee(null);
    setVerifiedPin(null);
  };

  return {
    // State
    selectedEmployee, setSelectedEmployee,
    showPinModal, setShowPinModal,
    showActionModal, setShowActionModal,
    showConfirmation, setShowConfirmation,
    confirmationMessage, confirmationWarning,
    pin, setPin,
    pinError, setPinError,
    verifyingPin, verifiedPin,
    processing,
    // Flow actions
    startFlow,
    handlePinDigit, handlePinBackspace, handlePinClear,
    handleVerifyPin, handleCancelPin,
    handleClockAction, handleCancelAction,
  };
}
